<template>
  <validation-observer
    class="view-content creator-view"
    ref="divisions-selection-form"
    tag="div"
    v-slot="{ invalid, errors }">
    <div
      v-if="!loaded || isStepChangeInProgress"
      class="loading-overlay">
      <div class="loader">
        <span>Indlæsning af data&hellip;</span>
      </div>
    </div>
    <h1 class="sr-only">Design dine skydelåger</h1>

    <wardrobe-preview>
      <template v-slot:content>
        <div
          v-if="loaded"
          class="wardrobe-layout"
          :data-doors-count="doorsNumber">
          <div
            class="wardrobe-front"
            v-bind:style="{ 'aspect-ratio': (wardrobeWidth / wardrobeHeight) }">

            <validation-provider
              v-for="(door, index) in doorsNumber"
              :key="'door-' + index + '-'"
              rules="required"
              v-slot="{ errors }"
              tag="div"
              class="door-part-validation">

              <div
                :class="{
                  'wardrobe-door selectable-door-part': true,
                  'is-active': selectedDoorIndex === index,
                  'is-invalid': errors.length
                }"
                :active="selectedDoorIndex === index"
                @click.stop.prevent="setSelectedDoor(index)">
                <input
                  type="hidden"
                  :name="'door-division-' + index"
                  v-model="data.doorsItems[index].division_id" />

                <div
                  v-for="n in getDivisionsNumber(data.doorsItems[index].division_id)"
                  :key="'door-' + index + '-part-' + n + '-display'"
                  class="door-part">
                </div>

                <span class="wardrobe-door-text">Vælg døre-deling</span>
              </div>
            </validation-provider>
          </div>
        </div>
      </template>
    </wardrobe-preview>

    <right-sidebar :view-local-state="localState">
      <template v-slot:header>
        <h2>Skydelåger design</h2>
      </template>
      <template v-slot:content>
        <div
          v-if="loaded"
          id="doorsDivisionForm">

          <div
            v-if="invalid"
            class="invalid-feedback text-center mb-2">
            Vælg venligst dørinddeling.
          </div>

          <div
            class="grid-cols-1"
            :key="'division-for-door-' + selectedDoorIndex">
            <c-form-radio
              v-for="(option, optionIndex) in availableOptionsDoorDivisions"
              :key="optionIndex"
              class="door-division-wrapper ml-auto mr-auto"
              :withImage="true"
              :value="option.id"
              v-model="data.doorsItems[selectedDoorIndex].division_id"
              :disabled="option.disabled">
              <img
                class="door-count-img"
                :style="{'height': '100%'}"
                :src="require('@/assets/images/svg/divide' + option.divide + '.svg')"
                :alt="'Dør-deling - ' + option.divide" />
              <span class="c-form-radio-label door-divisions-name">{{ option.name }}</span>
            </c-form-radio>
          </div>
        </div>
      </template>

      <template v-slot:footer>
        <navigation-buttons
          :form-id="'doorsDivisionForm'"
          @nextBtnClicked="validateForm"
          @backBtnClicked="goBack" />
      </template>
    </right-sidebar>
  </validation-observer>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import FormUtils from '@/utils/FormUtils.js';
import StateUtils from '@/utils/StateUtils.js';

export default {
  name: 'wardrobe-doors-division',
  computed: {
    ...mapGetters('creator', [
      'wardrobeHeight',
      'wardrobeWidth',
      'doorsNumber',
      'wardrobeSoftClose'
    ]),
    availableOptionsDoorDivisions () {
      return this.$store.getters['apiData/getAvailableOptionsDoorDivisions'](this.wardrobeHeight)
    },
    localState () {
      return {
        doors: {
          items: this.getUpdatedDoorFillings(),
          number: this.doorsNumber
        }
      };
    }
  },
  data () {
    return {
      data: {
        doorsItems: []
      },
      isStepChangeInProgress: false,
      loaded: false,
      loadError: false,
      selectedDoorIndex: 0
    }
  },
  mounted () {
    this.data.doorsItems = JSON.parse(JSON.stringify(this.$store.getters['creator/doorsItems']));

    let biggestDivideNumber = this.availableOptionsDoorDivisions.reduce((prev, curr) => prev.divide > curr.divide ? prev : curr);

    for (let i = 0; i < this.data.doorsItems.length; i++) {
      if (!this.data.doorsItems[i].division_id) {
        this.data.doorsItems[i].division_id = biggestDivideNumber.id;
      } else if (!this.availableOptionsDoorDivisions.find(item => item.id === this.data.doorsItems[i].division_id)) {
        this.data.doorsItems[i].division_id = biggestDivideNumber.id;
      }
    }

    this.loaded = true;
  },
  methods: {
    setSelectedDoor (selectedIndex) {
      Vue.set(this, 'selectedDoorIndex', selectedIndex);
    },
    validateForm () {
      Vue.set(this, 'isStepChangeInProgress', true);
      FormUtils.validate(this.$refs['divisions-selection-form'], this.goNextStep, this.handleStepChangeStop);
    },
    handleStepChangeStop () {
      Vue.set(this, 'isStepChangeInProgress', false);
    },
    async saveState (shouldMarkStepCompleted = false) {
      let isStepCompleted = this.$store.getters['creator/getIsStepCompleted']('wardrobeDoorsDivision');
      if (isStepCompleted) {
        if (this.areChangesMade()) {
          this.$store.commit('creator/resetSpecificSteps', ['wardrobeDoorsMaterial']);
        }
      }
      this.data.doorsItems = this.getUpdatedDoorFillings();

      await this.$nextTick();
      this.$store.commit('creator/setDoorsItems', this.data.doorsItems);

      if (shouldMarkStepCompleted) {
        this.$store.commit('creator/setStepCompletion', { stepName: 'wardrobeDoorsDivision', stepCompleted: true });
      }

      await this.$nextTick();
    },
    async goNextStep () {
      await this.saveState(true);

      this.handleStepChangeStop();
      this.$router.push({ name: 'wardrobeDoorsMaterial' });
    },
    async goBack () {
      Vue.set(this, 'isStepChangeInProgress', true);
      await this.saveState();

      this.handleStepChangeStop();
      this.$router.push({ name: 'wardrobeDoorsNumber' });
    },
    getDivisionsNumber (divisionID) {
      if (!divisionID) {
        return 1;
      }

      let divisionFound = this.availableOptionsDoorDivisions.find(item => item.id === divisionID);

      if (divisionFound && divisionFound.divide) {
        return divisionFound.divide;
      }
      return 1;
    },
    getUpdatedDoorFillings () {
      let output = JSON.parse(JSON.stringify(this.data.doorsItems));

      for (let i = 0; i < output.length; i++) {
        let fillingsCount = output[i].fillings.length;
        let divisionsNumber = this.getDivisionsNumber(output[i].division_id);

        if (fillingsCount > divisionsNumber) {
          output[i].fillings.splice(divisionsNumber);
        } else {
          while (output[i].fillings.length < divisionsNumber) {
            output[i].fillings.push({ filling_id: 0 });
          }
        }
      }

      return output;
    },
    areChangesMade () {
      return StateUtils.isDoorsDivisionChanged(this.localState, this.$store.getters['creator/creatorWardrobeData']);
    }
  }
};
</script>
